import { consume } from '@lit/context';
import { localized, t } from '@weavedev/lit-i18next';
import { LitElement, css, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';

import { PriceBreakdownDto } from '@/api-sdk';
import { iconCaret } from '@/components/icons';
import {
  ConfiguratorContextType,
  GlobalComponentType,
  configuratorGlobal,
  global,
} from '@/context';
import { BREAKPOINT_MEDIUM_PX } from '@/settings';
import { baseStyles, loadingStyles, rootVariablesStyles } from '@/styles';
import { DateType } from '@/types';
import { elephantOrderDateTemplate } from '@/utils';

@customElement('fg-configurator-mobile-summary')
@localized()
export class FgConfiguratorMobileSummary extends LitElement {
  @property({ type: String })
  totalsExclVat?: string;

  @property({ type: Boolean })
  isLoading = false;

  /**
   * Object containing the price breakdown data, and is used to render the price breakdown data & summary
   */
  @property({ type: Object })
  priceBreakdown?: PriceBreakdownDto;

  @consume({ context: global.context })
  globalContext!: GlobalComponentType;

  @consume({ context: configuratorGlobal.context, subscribe: true })
  configuratorContext!: ConfiguratorContextType;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.dispatchPriceDetailsEvent);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.dispatchPriceDetailsEvent);
  }

  dispatchPriceDetailsEvent() {
    this.dispatchEvent(new CustomEvent('open-price-details'));
  }

  render() {
    return html`
      <div
        class="${classMap({
          loading: this.isLoading,
        })} configurator-mobile-summary"
        @click=${this.dispatchPriceDetailsEvent}
      >
        <span class="totals-label no-blur">
          ${t('labels.totalPriceExclVat')} ${iconCaret}
        </span>
        <strong class="totals">${this.totalsExclVat || '-'}</strong>
      </div>
      ${this.priceBreakdown?.expectedShippingDate
        ? html`
            <div class="expected-shipping-date">
              ${elephantOrderDateTemplate(
                this.priceBreakdown?.expectedShippingDate,
                this.globalContext.locale,
                DateType.SHIPPING,
                true,
                this.configuratorContext.hspEnabled,
              )}
            </div>
          `
        : nothing}
    `;
  }

  static styles = [
    baseStyles,
    rootVariablesStyles,
    loadingStyles,
    css`
      .configurator-mobile-summary {
        display: flex;
        padding: 0.5rem 1rem;
        border-top: 1px solid var(--fg-gray--300, var(--fg-fallback-gray--300));
        justify-content: space-between;
      }

      .expected-shipping-date {
        padding: 0rem 1rem 0.5rem 1rem;

        .expected-shipping-content {
          display: flex;
          justify-content: space-between;
          width: 100%;
        }

        .shipping-date {
          display: flex;
          justify-content: space-between;

          date {
            display: flex;
          }
        }

        .info-shipping-date {
          display: flex;
          align-items: center;
          margin-left: 0.25rem;
          color: var(--fg-secondary, var(--fg-fallback-secondary));
        }
      }

      span {
        font-size: 0.875rem;
      }

      .totals-label {
        display: flex;
        align-items: center;
        gap: 8px;
        color: var(--fg-secondary, var(--fg-fallback-secondary));
        text-decoration: underline;
      }

      @media screen and (min-width: ${BREAKPOINT_MEDIUM_PX}px) {
        :host {
          display: none;
        }
      }
    `,
  ];
}
